import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { ListBalance } from '@shared/model'
import type { DisciplineBalancesFilterParams, DisciplineBalancesFilters } from '../model/interfaces'

import { getFirstQueryValue, normalizeArrayQuery } from '@shared/lib/routes'

/** Array filters that links from app-main encode as `key[0]=…`. */
export const DISCIPLINE_BALANCES_ARRAY_QUERY_KEYS: readonly string[] = ['reason']

const FILTER_QUERY_KEYS: readonly string[] = ['reason', 'responsible']

export const emptyDisciplineBalancesFilters = (): DisciplineBalancesFilters => ({
  reasons: [],
  responsible: null,
})

/** Values the lists do not name are dropped: the API answers them with a 422. */
export const queryToDisciplineBalancesFilters = (
  query: Record<string, unknown>,
  lists: ListBalance | null,
): DisciplineBalancesFilters => {
  const reasons = normalizeArrayQuery(query.reason) ?? []
  const responsible = getFirstQueryValue(query.responsible)

  return {
    reasons: lists ? reasons.filter(reason => reason in lists.reason) : reasons,
    responsible: responsible && (!lists || responsible in lists.responsible) ? responsible : null,
  }
}

export const getDisciplineBalancesFilterParams = (filters: DisciplineBalancesFilters): DisciplineBalancesFilterParams => ({
  reason: filters.reasons.length ? filters.reasons : undefined,
  responsible: filters.responsible || undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const disciplineBalancesFiltersToQuery = (filters: DisciplineBalancesFilters): LocationQueryRaw => ({
  ...getDisciplineBalancesFilterParams(filters),
})

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

/** The query rewritten the way `filters` reads it, or `null` when it already is. */
export const getCanonicalDisciplineBalancesQuery = (
  query: LocationQuery,
  filters: DisciplineBalancesFilters,
): LocationQueryRaw | null => {
  const canonical = disciplineBalancesFiltersToQuery(filters)
  const isCanonical = FILTER_QUERY_KEYS.every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}
